export interface Fragrance {
  fragranceId: string;
  name: string;
  house: string;
  notes?: string[];
  topNotes?: string[];
  middleNotes?: string[];
  baseNotes?: string[];
  description?: string;
}

export interface ScentSignals {
  preferredDescriptors?: string[];
  avoidedDescriptors?: string[];
  avoidedFamilies?: string[];
  preferredSeasons?: string[];
}

export const guessNoteFamily = (note: string): string | null => {
  const n = note.toLowerCase();
  if (['rose','jasmine','lily','violet','iris','tuberose','peony','gardenia','magnolia','neroli','ylang','orchid','freesia','carnation','geranium'].some(w => n.includes(w))) return 'Florals';
  if (['cedar','sandalwood','vetiver','oud','patchouli','oak','birch','pine','wood','guaiac','agarwood','hinoki','spruce','fir','cypriol','rosewood'].some(w => n.includes(w))) return 'Woods';
  if (['bergamot','lemon','orange','grapefruit','lime','mandarin','citrus','yuzu','clementine','petitgrain','citron','lemon verbena','citronella'].some(w => n.includes(w))) return 'Citrus';
  if (['pepper','cardamom','cinnamon','clove','nutmeg','ginger','saffron','cumin','coriander','elemi'].some(w => n.includes(w))) return 'Spices';
  if (['amber','benzoin','frankincense','myrrh','resin','labdanum','incense','opoponax','cistus','ambrox','ambroxan','balsam','olibanum','elemi'].some(w => n.includes(w))) return 'Resins & Orientals';
  if (['musk','ambrette','ambergris','civet','castoreum','cashmeran'].some(w => n.includes(w))) return 'Musks';
  if (['grass','moss','fern','aquatic','marine','sea','green','basil','mint','sage','lavender','rosemary','thyme','galbanum','hay','oakmoss','angelica','juniper'].some(w => n.includes(w))) return 'Greens & Aquatics';
  if (['apple','peach','pear','cherry','plum','berry','fig','mango','coconut','lychee','melon','cassis','blackcurrant','raspberry','strawberry','apricot','rhubarb'].some(w => n.includes(w))) return 'Fruits';
  if (['tobacco','vanilla','chocolate','caramel','honey','coffee','almond','praline','tonka','rum','leather','cacao','cocoa','candy','sugar','tea'].some(w => n.includes(w))) return 'Tobacco & Gourmands';
  return null;
};

export const computeSignalModifier = (frag: Fragrance, signals: ScentSignals): number => {
  if (!signals) return 0;
  const desc = (frag.description || '').toLowerCase();
  const allNotes = Array.from(new Set([
    ...(frag.notes || []), ...(frag.topNotes || []),
    ...(frag.middleNotes || []), ...(frag.baseNotes || []),
  ]));
  const fragFamilies = allNotes.map(n => guessNoteFamily(n)).filter(Boolean) as string[];

  let adj = 0;
  for (const d of (signals.preferredDescriptors || [])) {
    if (desc.includes(d.toLowerCase())) adj += 10;
  }
  adj = Math.min(adj, 25);
  for (const d of (signals.avoidedDescriptors || [])) {
    if (desc.includes(d.toLowerCase())) adj -= 28;
  }
  for (const family of (signals.avoidedFamilies || [])) {
    if (fragFamilies.some(f => f === family)) adj -= 22;
  }
  return Math.max(-60, Math.min(25, adj));
};

export const computeCompatScore = (
  frag: Fragrance,
  topFamilies: Array<{ category: string; pct: number }>,
  likedNotes: Map<string, number>
): number | null => {
  if (!topFamilies || topFamilies.length === 0) return null;
  const allNotes = Array.from(new Set([
    ...(frag.notes || []), ...(frag.topNotes || []),
    ...(frag.middleNotes || []), ...(frag.baseNotes || []),
  ]));
  if (allNotes.length === 0) return null;

  let score = 0;
  let maxScore = 0;
  for (const note of allNotes) {
    const family = guessNoteFamily(note);
    if (family !== null) {
      const rank = topFamilies.findIndex(f => f.category === family);
      const familyPts = rank === 0 ? 3 : rank === 1 ? 2 : rank === 2 ? 1 : 0;
      score += familyPts;
      maxScore += 3;
    }
    const noteWeight = likedNotes.get(note.toLowerCase()) || 0;
    if (noteWeight > 0) { score += Math.min(noteWeight, 2); maxScore += 2; }
  }
  if (maxScore === 0) return null;
  return Math.round(Math.max(0, Math.min(100, (score / maxScore) * 100)));
};

export const buildLikedNotes = (
  journalEntries: Array<{ fragranceId: string; rating?: string }>,
  catalog: Fragrance[],
  noteSentimentMap?: Record<string, number>
): Map<string, number> => {
  const map = new Map<string, number>();
  // Fragrance-level rating weights all notes of that fragrance
  for (const entry of journalEntries) {
    const weight = entry.rating === 'love' ? 3 : entry.rating === 'like' ? 1 : entry.rating === 'dislike' ? -2 : 0;
    if (weight === 0) continue;
    const frag = catalog.find(f => f.fragranceId === entry.fragranceId);
    if (!frag) continue;
    const notes = [...(frag.notes || []), ...(frag.topNotes || []), ...(frag.middleNotes || []), ...(frag.baseNotes || [])];
    for (const note of notes) {
      const key = note.toLowerCase();
      map.set(key, (map.get(key) || 0) + weight);
    }
  }
  // Note-level sentiment from journal free-text (AI-extracted at profile refresh) — more precise, higher weight
  if (noteSentimentMap) {
    for (const [note, sentiment] of Object.entries(noteSentimentMap)) {
      const key = note.toLowerCase();
      // Multiply by 2 so explicit note mentions outweigh indirect fragrance-level signals
      map.set(key, (map.get(key) || 0) + sentiment * 2);
    }
  }
  return map;
};

export const computeFinalScore = (
  frag: Fragrance,
  topFamilies: Array<{ category: string; pct: number }>,
  likedNotes: Map<string, number>,
  scentSignals: ScentSignals | null,
  directRating?: string
): number | null => {
  const base = computeCompatScore(frag, topFamilies, likedNotes);
  if (base === null) return null;
  const modifier = scentSignals ? computeSignalModifier(frag, scentSignals) : 0;
  let score = Math.round(Math.max(0, Math.min(100, base + modifier)));
  if (directRating === 'love') score = Math.max(score, 88);
  else if (directRating === 'like') score = Math.max(score, 70);
  else if (directRating === 'dislike') score = Math.min(score, 18);
  return score;
};
